import { Box, Button, Flex, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import * as React from "react";
import { Fragment, useState } from "react";
import { t } from "i18next";
import { H2 } from "components/text/headings";
import {
  indirectEmissionsKeys,
  useAddIndirectEmissionsItem,
  useDefaultOrganizationQuestionnaireConfig,
  useDeleteIndirectEmissionsItem,
  useFinalizeQuestionnaire,
  useGetIndirectEmissionsItems,
  useUpdateIndirectEmissionsItem,
} from "../../../../hooks/query";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { DimensionItemModal, DimensionsGrid } from "../DimensionsGrid";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { mapEmissionsFormDataToElectricityApiData } from "../../helpers";

function IndirectEmissions({
  organizationStructure,
  defaultValues,
  handleTabChange,
  hasNextScope,
  hasPrevScope,
  isPreview,
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const {
    isOpen: isDimensionItemModalOpen,
    onOpen: onDimensionItemModalOpen,
    onClose: onDimensionItemModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose,
  } = useDisclosure();

  const { id: questionnaireId, dimensionItems } = defaultValues;

  const buttonsWidth = useBreakpointValue({
    base: "24",
    md: "32",
  });

  const ELECTRICITY_SECTION = "electricity";

  const [selectedItem, setSelectedItem] = useState(null);
  const [dimensionItemModalData, setDimensionItemModalData] = useState(null);
  const [sectionsSize, setSectionsSize] = useState({});

  const changeTab = (direction) => {
    if (direction === "previous") {
      handleTabChange((prev) => prev - 1);
    } else if (direction === "next") {
      handleTabChange((prev) => prev + 1);
    }
  };

  const { mutate: finalize } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutate: addItem } = useAddIndirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutate: updateItem } = useUpdateIndirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutate: deleteItem } = useDeleteIndirectEmissionsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const handleFinalizeConfirmation = () => {
    finalize(questionnaireId);
    onConfirmDialogClose();
  };

  const handleAddItemClick = () => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "two",
      fieldArrayName: ELECTRICITY_SECTION,
      consumptionTypesAttr: "electricityTypes",
    });
    onDimensionItemModalOpen();
  };

  const handleEditItemClick = (item) => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "two",
      fieldArrayName: ELECTRICITY_SECTION,
      consumptionTypesAttr: "electricityTypes",
      editData: item,
    });
    onDimensionItemModalOpen();
  };

  const handleDeleteItemClick = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id, type: ELECTRICITY_SECTION });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id, type: selectedItem.type });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleDimensionItemModalSubmit = (data) => {
    const apiData = mapEmissionsFormDataToElectricityApiData(data[ELECTRICITY_SECTION]);
    dispatch(showAppSpinner());
    if (dimensionItemModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0],
        type: ELECTRICITY_SECTION,
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData,
        type: ELECTRICITY_SECTION,
      });
    }
  };

  const handleDimensionItemModalClose = () => {
    setDimensionItemModalData(null);
    onDimensionItemModalClose();
  };

  const handleSectionSizeChange = (section, size) => {
    setSectionsSize({
      ...sectionsSize,
      [section]: size,
    });
  };

  return (
    <Fragment>
      {isPreview &&
      sectionsSize &&
      Object.keys(sectionsSize).length === 1 &&
      typeof Object.keys(sectionsSize).find((key) => sectionsSize[key] > 0) === "undefined" ? (
        <H2>{t("noDataAvailable")}</H2>
      ) : (
        <Box marginBlockStart="10" overflow="auto">
          {questionnaireId && organizationStructure && (
            <DimensionsGrid
              questionnaireId={questionnaireId}
              scope="two"
              organizationStructure={organizationStructure}
              sectionType={ELECTRICITY_SECTION}
              consumptionTypesAttr={"electricity"}
              getItemsFunction={useGetIndirectEmissionsItems}
              handleAddItem={() => handleAddItemClick()}
              handleEditItem={(dataItem) => handleEditItemClick(dataItem)}
              handleDeleteItem={(dataItem) => handleDeleteItemClick(dataItem)}
              isPreview={isPreview}
              onDataItemsChange={handleSectionSizeChange}
            />
          )}
        </Box>
      )}
      {!isPreview && (
        <Flex
          marginBlockStart="10"
          position="sticky"
          bottom="0"
          backgroundColor="#fff"
          borderColor="esg_gray.100"
          bordersyle="solid"
          borderTopWidth="1px"
          paddingBlock="4"
          justifyContent="space-between"
          zIndex="10"
        >
          <Flex gap="2">
            {hasPrevScope && (
              <Button
                colorScheme="white"
                color="esg_lightgreen"
                border={"1px solid"}
                borderColor={"esg_lightgreen"}
                width={buttonsWidth}
                type="submit"
                onClick={() => {
                  changeTab("previous");
                }}
              >
                {t("buttons.previous")}
              </Button>
            )}
            {hasNextScope && (
              <Button
                colorScheme="white"
                color="esg_lightgreen"
                border={"1px solid"}
                borderColor={"esg_lightgreen"}
                width={buttonsWidth}
                type="submit"
                onClick={() => {
                  changeTab("next");
                }}
              >
                {t("buttons.next")}
              </Button>
            )}
          </Flex>
          <Button
            colorScheme="customScheme"
            width={buttonsWidth}
            onClick={() => {
              onConfirmDialogOpen();
            }}
          >
            {t("buttons.finalize")}
          </Button>
        </Flex>
      )}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {isDimensionItemModalOpen && dimensionItemModalData !== null && (
        <DimensionItemModal
          modalData={dimensionItemModalData}
          isOpen={isDimensionItemModalOpen}
          onClose={handleDimensionItemModalClose}
          onSubmit={handleDimensionItemModalSubmit}
          scope="two"
        />
      )}
    </Fragment>
  );
}

export default IndirectEmissions;
